import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
    issueFunds,
    fetchUsers,
} from '../../../features/users/usersSlice'
import { fetchFundSummary } from '../../../features/fundManagement/fundManagementSlice'

import './IssueFundsForm.css'
import CTextField from '../../../components/UI/CTextField'
import CButton from '../../../components/UI/CButton'
import Icon from '../../../components/UI/Icon'

/**
 * Reusable component for issuing funds to selected users
 * Handles all issue funds logic internally
 */
const IssueFundsForm = ({
    selectedUsers = [],
    onClose,
    onSuccess,
    onError,
    onTopUpFunds,
    currency = 'ZAR',
}) => {
    const dispatch = useDispatch()
    const { loading } = useSelector((state) => state.users)
    const { balance } = useSelector((state) => state.fundManagement.wallet)

    const [amount, setAmount] = useState(0)
    const [validationError, setValidationError] = useState(false)
    const [errorText, setErrorText] = useState('')

    // Reset form when component mounts
    useEffect(() => {
        setAmount(0)
        setValidationError(false)
        setErrorText('')
        dispatch(fetchFundSummary())
    }, [])

    const formatCurrency = (value) => {
        const currencyMap = {
            'ZAR': 'en-ZA',
            'USD': 'en-US',
        }

        return new Intl.NumberFormat(currencyMap[currency] || 'en-US', {
            style: 'currency',
            currency: currency,
        }).format(value)
    }

    const handleAmountChange = (value) => {
        setAmount(value)
        if (validationError) {
            setValidationError(false)
            setErrorText('')
        }
    }

    const handleCancel = () => {
        setAmount(0)
        setValidationError(false)
        setErrorText('')
        if (onClose) onClose()
    }

    const handleSubmit = async () => {
        const parsedAmount = parseFloat(amount)

        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            setValidationError(true)
            setErrorText('Please enter a valid amount greater than 0')
            return
        }

        if (selectedUsers.length === 0) {
            return;
        }

        try {
            const participantIds = selectedUsers.map(user => user.participantid)
            const result = await dispatch(issueFunds({ participantIds: participantIds, amount: parsedAmount })).unwrap()

            // Refresh users list
            dispatch(fetchUsers())

            // Reset form
            setAmount(0)
            setValidationError(false)
            setErrorText('')

            if (onSuccess) onSuccess({ amount: parsedAmount, userCount: selectedUsers.length })
            if (onClose) onClose()
        } catch (err) {
            if (onError) onError(err)
        }
    }

    const totalAmount = parseFloat(amount) || 0
    const totalDistribution = totalAmount * selectedUsers.length


    const handleTopUpFundsClick = () => {
        if (onTopUpFunds) onTopUpFunds()
    };

    return (
        <div className="issue-funds-form">
            <span className="text-4">
                Enter the amount you’d like to issue to the selected users.
            </span>

            <div className="current-balance-block">
                <div className="current-balance-info">
                    <div className="current-balance-col">
                        <span className="text-5">Current Balance</span>
                        <span className="header-3">{formatCurrency(balance)}</span>
                    </div>
                    <div className="icon-wrapper">
                        <Icon name="credit-card-sync" />
                    </div>
                </div>
                <div className='divider'></div>

                <CButton
                    label="Top up funds"
                    onClick={handleTopUpFundsClick}
                    variant="outlined"
                    color="secondary"
                    disabled={loading.issueFunds}
                />
            </div>

            <span className='form-sub-title header-4'>Amount per user</span>

            <CTextField
                label={`Amount (${currency})`}
                type="number"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0.00"
                fullWidth
                autoFocus
                disabled={loading.issueFunds}
                helperText={
                    validationError ?
                        errorText
                        :
                        `Total to be issued: ${formatCurrency(totalDistribution)} across ${selectedUsers.length} user(s)`
                }
                error={validationError}
            />

            <span className='form-sub-title header-4'>Funds summary</span>

            <div className="funds-summary">
                <div className="summary-row">
                    <span className="text-4">Number of users</span>
                    <span className="header-4">{selectedUsers.length}</span>
                </div>
                <div className="summary-row">
                    <span className="text-4">Amount per user</span>
                    <span className="header-4">{formatCurrency(totalAmount)}</span>
                </div>
                <div className="summary-row">
                    <span className="header-4">Total issued</span>
                    <span className="header-4">{formatCurrency(totalDistribution)}</span>
                </div>
                <div className="divider"></div>
                <div className="summary-row">
                    <span className="header-4">New balance of funds</span>
                    <span className="header-4">{formatCurrency(balance - totalDistribution)}</span>
                </div>
            </div>

            <div className="form-actions">
                <CButton
                    label="Cancel"
                    onClick={handleCancel}
                    variant="outlined"
                    color="secondary"
                    disabled={loading.issueFunds}
                />
                <CButton
                    label="Issue Funds"
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    loading={loading.issueFunds}
                />
            </div>
        </div>
    )
}

export default IssueFundsForm
